/**
 * Day by day, against the same weekday (DEC-28 C3).
 *
 * The fleet grows in waves, so the page leads with charges per charge point,
 * which stays flat when a wave adds stations and moves only when charging
 * itself changes. Each day is compared with the median of the four same
 * weekdays before it, never with the single week before: one incident day
 * would otherwise make the next week look like a collapse.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { ArrowDownloadRegular } from '@fluentui/react-icons'
import {
  Chip,
  DataTableShell,
  EmptyState,
  KpiCard,
  LoadingState,
  SectionCard,
  StatusBadge,
} from '../platform'
import { ApiError, api } from '../api'
import type { Daily } from '../api'
import { problemText } from '../auth'
import { TimeChart } from './Charts'
import { useChartPalette } from './chartPalette'
import {
  METRICS,
  type Metric,
  dayLabel,
  deltaPercent,
  deltaText,
  deltaTone,
  lastWholeDay,
  metricOf,
  toCsv,
  valueText,
} from './dailyReading'

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  intro: { color: tokens.colorNeutralForeground2, maxWidth: '860px', margin: 0 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  kpis: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  scroll: { overflowX: 'auto' },
  table: { minWidth: '980px' },
  number: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  up: { color: tokens.colorPaletteGreenForeground1, fontWeight: tokens.fontWeightSemibold },
  down: { color: tokens.colorPaletteRedForeground1, fontWeight: tokens.fontWeightSemibold },
  flat: { color: tokens.colorNeutralForeground2 },
  dayCell: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, whiteSpace: 'nowrap' },
})

export default function DayByDay() {
  const styles = useStyles()
  const palette = useChartPalette()
  const [data, setData] = useState<Daily | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [metric, setMetric] = useState<Metric>('charges_per_charge_point')

  useEffect(() => {
    api
      .get<Daily>('/daily?days=42')
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? problemText(e) : String(e)))
  }, [])

  const chosen = METRICS.find((m) => m.key === metric)!
  const last = useMemo(() => (data ? lastWholeDay(data.days) : null), [data])

  if (error) return <EmptyState title="The days cannot be read" description={error} />
  if (!data) return <LoadingState label="Reading the days" />

  const headline = (key: Metric, label: string, ratio: boolean) => {
    const value = metricOf(last, key)
    const base = metricOf(last?.baseline, key)
    const delta = deltaPercent(value, base)
    return (
      <KpiCard
        key={key}
        title={last ? `${label}, ${dayLabel(last)}` : label}
        value={valueText(value, ratio)}
        caption={
          base === null
            ? 'no earlier same weekday to compare'
            : `${deltaText(delta)} against ${valueText(base, ratio)}, the median of the same weekdays`
        }
      />
    )
  }

  const recent = [...data.days].reverse().slice(0, 14)
  const toneClass = (delta: number | null) => styles[deltaTone(delta)]
  const download = () => {
    const url = URL.createObjectURL(new Blob([toCsv(data.days)], { type: 'text/csv' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `eas-day-by-day-${data.days.at(-1)?.day ?? 'days'}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className={styles.page}>
      <p className={styles.intro}>
        Each day against the median of the four same weekdays before it. The fleet is still growing, so the
        figures per charge point are the fair comparison: they stay flat when a migration wave adds stations
        and move only when charging itself changes. Days are Berlin days.
      </p>

      <div className={styles.chips} role="group" aria-label="What to show">
        {METRICS.map((m) => (
          <Chip key={m.key} selected={metric === m.key} onClick={() => setMetric(m.key)}>
            {m.label}
          </Chip>
        ))}
      </div>

      <section aria-label="The last whole day" className={styles.kpis}>
        {headline('charges_per_charge_point', 'Charges per charge point', true)}
        {headline('starts', 'Charges started', false)}
        {headline('charge_points', 'Active charge points', false)}
        {headline('asks_per_charge', 'Asks per charge', true)}
      </section>

      <SectionCard title={chosen.title} description="The bars are the days, the line the median of the same weekdays before each.">
        <TimeChart
          title={chosen.title}
          // Noon UTC of each Berlin day: the same date in Berlin in either
          // season, so no bar moves to the day before.
          buckets={data.days.map((d) => `${d.day}T12:00:00Z`)}
          bucketSeconds={86400}
          zone="Europe/Berlin"
          format={(value) => valueText(value, chosen.ratio)}
          bars={[{ key: metric, name: chosen.label, colour: palette.approved, values: data.days.map((d) => metricOf(d, metric)) }]}
          lines={[{
            key: 'baseline',
            name: 'Median of the same weekdays',
            colour: palette.p95,
            values: data.days.map((d) => metricOf(d.baseline, metric)),
          }]}
        />
      </SectionCard>

      <DataTableShell
        title="Last two weeks, against the same weekday"
        controls={
          <Button icon={<ArrowDownloadRegular />} onClick={download}>
            Export CSV
          </Button>
        }
      >
        <div className={styles.scroll}>
          <Table aria-label="Days" className={styles.table} size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Day</TableHeaderCell>
                <TableHeaderCell className={styles.number}>Asks</TableHeaderCell>
                <TableHeaderCell className={styles.number}>Change</TableHeaderCell>
                <TableHeaderCell className={styles.number}>Charges started</TableHeaderCell>
                <TableHeaderCell className={styles.number}>Change</TableHeaderCell>
                <TableHeaderCell className={styles.number}>Charge points</TableHeaderCell>
                <TableHeaderCell className={styles.number}>Change</TableHeaderCell>
                <TableHeaderCell className={styles.number}>Charges per point</TableHeaderCell>
                <TableHeaderCell className={styles.number}>Asks per charge</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recent.map((row) => {
                const dA = deltaPercent(row.asks, row.baseline?.asks)
                const dS = deltaPercent(row.starts, row.baseline?.starts)
                const dP = deltaPercent(row.charge_points, row.baseline?.charge_points)
                return (
                  <TableRow key={row.day}>
                    <TableCell>
                      <span className={styles.dayCell}>
                        {dayLabel(row)}
                        {row.partial ? <StatusBadge status="warning" label="day not over" /> : null}
                      </span>
                    </TableCell>
                    <TableCell className={styles.number}>{valueText(row.asks, false)}</TableCell>
                    <TableCell className={`${styles.number} ${toneClass(dA)}`}>{deltaText(dA)}</TableCell>
                    <TableCell className={styles.number}>{valueText(row.starts, false)}</TableCell>
                    <TableCell className={`${styles.number} ${toneClass(dS)}`}>{deltaText(dS)}</TableCell>
                    <TableCell className={styles.number}>{valueText(row.charge_points, false)}</TableCell>
                    <TableCell className={`${styles.number} ${toneClass(dP)}`}>{deltaText(dP)}</TableCell>
                    <TableCell className={styles.number}>{valueText(row.charges_per_charge_point, true)}</TableCell>
                    <TableCell className={styles.number}>{valueText(row.asks_per_charge, true)}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      </DataTableShell>
    </div>
  )
}
